import { Button } from "@/components/ui/button";
import { ArrowRight, ChevronDown, Play, Check } from "lucide-react";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import heroImage from "@/assets/hero-video-ai.jpg";
import LogoCarousel from "./LogoCarousel";
import { VideoModal } from "./VideoModal";
import { useUploadedVideo } from "@/hooks/useUploadedVideo";
interface HeroSectionProps {
  content: {
    badge: string;
    headline: string;
    subheadline: string;
    benefits: readonly string[];
    cta: {
      primary: string;
      secondary: string;
    };
  };
}
const HeroSection = ({
  content
}: HeroSectionProps) => {
  const { currentVideo, loading } = useUploadedVideo("hero");

  useEffect(() => {
    // Check if script already exists to prevent duplicates
    const existingScript = document.querySelector('script[src*="typeform.com/next/embed.js"]');
    if (existingScript) return;
    
    // Defer loading slightly to prioritize critical resources
    const timeoutId = setTimeout(() => {
      const script = document.createElement('script');
      script.src = '//embed.typeform.com/next/embed.js';
      script.async = true;
      document.head.appendChild(script);
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, []);
  const openTypeform = () => {
    // @ts-ignore
    window.tf?.live?.open('01K52CKWVK6E3XFQHKJ9AH1AK2');
  };
  const scrollToForm = () => {
    const formElement = document.getElementById('embedded-typeform') || document.getElementById('embedded-typeform-mobile');
    if (formElement) {
      // Use requestAnimationFrame to avoid forced reflow
      requestAnimationFrame(() => {
        formElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      });
    }
  };
  return <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 md:pt-0 pb-12 bg-black">
      {/* Background Video */}
      <video
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        onLoadedData={(e) => {
          const v = e.currentTarget;
          if (v.paused) {
            v.play().catch(() => {
              // ignore autoplay block
            });
          }
        }}
        className="absolute inset-0 w-full h-full object-cover"
      >
        <source src={currentVideo?.publicUrl || "/videos/volym-studio-showreel.mp4"} type="video/mp4" />
      </video>
      
      {/* Subtle grid/raster effect */}
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: `
          linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px)
        `,
        backgroundSize: '50px 50px'
      }}></div>
      
      {/* 75% transparent overlay with existing background effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/75 via-gray-800/75 to-black/75"></div>
      <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-transparent to-black/75"></div>
      
      {/* Multiple radial light spots creating bokeh effect - adjusted opacity */}
      <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-gradient-radial from-brand-orange/12 via-brand-orange/4 to-transparent rounded-full blur-3xl"></div>
      <div className="absolute top-1/2 right-1/4 w-80 h-80 bg-gradient-radial from-brand-purple/10 via-brand-purple/3 to-transparent rounded-full blur-2xl"></div>
      <div className="absolute bottom-1/3 left-1/4 w-64 h-64 bg-gradient-radial from-brand-blue/6 via-brand-blue/2 to-transparent rounded-full blur-3xl"></div>
      <div className="absolute top-2/3 right-1/3 w-72 h-72 bg-gradient-radial from-brand-orange/8 via-brand-orange/2 to-transparent rounded-full blur-xl"></div>
      <div className="absolute top-1/6 right-1/2 w-48 h-48 bg-gradient-radial from-brand-purple/16 via-brand-purple/6 to-transparent rounded-full blur-2xl"></div>
      
      {/* Diagonal light streams - adjusted opacity */}
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-brand-orange/3 to-transparent transform rotate-12 scale-150"></div>
      <div className="absolute inset-0 bg-gradient-to-tl from-transparent via-brand-purple/2 to-transparent transform -rotate-6 scale-125"></div>
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-brand-blue/2 to-transparent transform rotate-3 scale-110"></div>
      
      {/* Original radial gradient behind h1 - adjusted opacity */}
      <div className="absolute top-1/2 left-0 lg:left-1/4 w-96 h-96 -translate-y-1/2 bg-gradient-radial from-white/6 via-gray-400/3 to-transparent rounded-full blur-3xl"></div>
      
      {/* Content */}
      <div className="relative z-10 container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="lg:grid lg:grid-cols-2 lg:gap-12 lg:gap-16 lg:items-center">
              {/* Left Column - Content */}
              <div className="text-center lg:text-left mb-8 lg:mb-0">
                <div className="inline-block px-3 py-1.5 md:px-4 md:py-2 bg-gray-800 border border-white/20 rounded-lg text-xs md:text-sm font-medium text-white mb-8 mt-4 shadow-[0_1px_3px_0_rgb(59_130_246_/_0.3)] backdrop-blur-sm">{content.badge}</div>
                
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                  <span className="hero-text">{content.headline}</span>
                </h1>
                
                {/* Benefits with check icons */}
                <div className="grid grid-cols-3 gap-4 mb-8 max-w-xl mx-auto lg:mx-0">
                  {content.benefits.map((benefit, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-white/80 flex-shrink-0" />
                      <span className="text-sm text-white/80">{benefit}</span>
                    </div>
                  ))}
                </div>
                
                {/* Logo Carousel efter bullet list */}
                <div className="mb-6">
                  <LogoCarousel />
                </div>
                
                <p className="text-base md:text-lg lg:text-xl text-white/70 mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
                  {content.subheadline}
                </p>
                
                <div className="flex flex-col sm:flex-row gap-6 justify-center lg:justify-start items-center mb-8 lg:mb-0">
                  <VideoModal
                    videoUrl="/videos/volym-studio-showreel.mp4"
                    title="Volym Studio Showreel"
                    className="max-w-[1280px]"
                    trigger={
                      <Button size="lg" className="bg-gradient-primary border-0 hover:opacity-90 text-lg px-8 py-4 h-auto">
                        <Play className="mr-2 w-5 h-5" />
                        Se film
                      </Button>
                    }
                  />
                  
                  <div className="flex items-center gap-2 text-lg text-white">
                    {content.cta.primary}
                    <ArrowRight className="w-5 h-5 hidden lg:block" />
                    <ChevronDown className="w-5 h-5 block lg:hidden" />
                  </div>
                </div>
              </div>
              
              {/* Right Column - Typeform (Desktop only) */}
              <div className="hidden lg:block">
                <div id="embedded-typeform" data-tf-live="01K52CKWVK6E3XFQHKJ9AH1AK2" className="h-[500px] w-full rounded-lg overflow-hidden shadow-lg bg-white"></div>
              </div>
            </div>
            
            {/* Mobile Typeform - positioned after the CTA text */}
            <div className="lg:hidden mt-8 pb-8">
              <div id="embedded-typeform-mobile" data-tf-live="01K52CKWVK6E3XFQHKJ9AH1AK2" className="h-96 w-full rounded-lg overflow-hidden shadow-lg bg-white"></div>
            </div>
          </div>
      </div>
      
      {/* Floating Elements */}
      <div className="absolute top-10 left-5 w-96 h-96 bg-brand-blue/8 rounded-full blur-3xl animate-float"></div>
      <div className="absolute bottom-10 right-5 w-80 h-80 bg-brand-blue/6 rounded-full blur-3xl animate-float" style={{
      animationDelay: "2s"
    }}></div>
      <div className="absolute top-1/3 left-1/4 w-72 h-72 bg-brand-purple/18 rounded-full blur-3xl animate-float" style={{
      animationDelay: "4s"
    }}></div>
    </section>;
};
export default HeroSection;